/**
 * LOT 2 — Traduction des erreurs d'API en objets exploitables par l'interface.
 *
 * POURQUOI UN MODULE SÉPARÉ : `ApiService.ts` importe axios, qui est publié en
 * ESM et n'est pas transpilé par le Jest de react-scripts. Tout test qui
 * touche `ApiService` fait échouer la suite entière avec « Cannot use import
 * statement outside a module ». En isolant la logique de traduction ici —
 * zéro dépendance — elle devient testable, et `DeviceManagement` peut
 * l'importer sans tirer la pile réseau.
 *
 * `ApiService.ts` ré-exporte tout ce fichier : les imports existants
 * continuent de fonctionner.
 */

/**
 * Codes de conflit renvoyés par l'API dans `detail.code`
 * (api/routes_devices.py, `_detail_conflit`). C'est un contrat : les changer
 * ici sans les changer là-bas casse silencieusement l'affichage.
 *
 *   mqtt_username_deja_utilise            -> blocage sec, un boîtier VIVANT
 *                                            porte déjà ce nom
 *   mqtt_username_libere_par_suppression  -> le nom a appartenu à un boîtier
 *                                            supprimé : reprise possible,
 *                                            sur confirmation explicite
 *   reactivation_boitier_supprime         -> restaurer ce boîtier créerait
 *                                            un doublon
 *   mqtt_username_invalide                -> forme invalide ou compte système
 */
export type CodeConflitMqtt =
  | 'mqtt_username_deja_utilise'
  | 'mqtt_username_libere_par_suppression'
  | 'reactivation_boitier_supprime'
  | 'mqtt_username_invalide';

/**
 * LOT 9 — le 409 n'est plus réservé aux conflits d'identifiant MQTT.
 *
 * `PUT /devices/{id}` rend aussi 409 avec `detail.code = 'version_perimee'`
 * quand quelqu'un a modifié la fiche entre-temps. Sans cette liste explicite,
 * `conflitMqttDepuisErreur` attrapait TOUT 409 porteur d'un `detail.code` —
 * un conflit de version se serait affiché à l'opérateur comme un conflit
 * d'identifiant MQTT, et l'aurait envoyé renommer un boîtier pour rien.
 */
const CODES_CONFLIT_MQTT: readonly string[] = [
  'mqtt_username_deja_utilise',
  'mqtt_username_libere_par_suppression',
  'reactivation_boitier_supprime',
  'mqtt_username_invalide',
];

/**
 * Conflit d'identifiant MQTT (HTTP 409, ou 400 pour un identifiant invalide).
 *
 * Elle existe parce qu'avec axios, `err.message` vaut « Request failed with
 * status code 409 » : afficher `err.message` supprime le message métier
 * renvoyé par l'API. L'exploitant voyait un code HTTP et devait deviner que
 * son nom de boîtier était déjà pris.
 */
export class ErreurConflitMqtt extends Error {
  readonly code: CodeConflitMqtt;
  readonly mqttUsername: string | null;
  readonly deviceId: number | null;
  readonly statut: number;

  constructor(
    code: CodeConflitMqtt,
    message: string,
    mqttUsername: string | null,
    deviceId: number | null,
    statut: number,
  ) {
    super(message);
    this.name = 'ErreurConflitMqtt';
    this.code = code;
    this.mqttUsername = mqttUsername;
    this.deviceId = deviceId;
    this.statut = statut;
    // Nécessaire pour que `instanceof` fonctionne après transpilation ES5.
    Object.setPrototypeOf(this, ErreurConflitMqtt.prototype);
  }

  /** L'identifiant est libre, mais il a appartenu à un boîtier supprimé :
   *  l'interface peut proposer de le reprendre, après confirmation. */
  get reprisePossible(): boolean {
    return this.code === 'mqtt_username_libere_par_suppression';
  }
}

/**
 * Traduit une erreur axios en `ErreurConflitMqtt` quand l'API a renvoyé un
 * `detail` OBJET. Rend `null` dans tous les autres cas — y compris un 409
 * dont le `detail` serait une chaîne : mieux vaut laisser passer l'erreur
 * générique que d'inventer un code auquel l'interface réagirait à tort.
 */
export function conflitMqttDepuisErreur(erreur: any): ErreurConflitMqtt | null {
  const reponse = erreur?.response;
  if (!reponse) return null;
  if (reponse.status !== 409 && reponse.status !== 400) return null;

  const detail = reponse.data?.detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  if (typeof detail.code !== 'string') return null;
  if (!CODES_CONFLIT_MQTT.includes(detail.code)) return null;

  return new ErreurConflitMqtt(
    detail.code as CodeConflitMqtt,
    typeof detail.message === 'string' && detail.message
      ? detail.message
      : "Conflit d'identifiant MQTT",
    typeof detail.mqtt_username === 'string' ? detail.mqtt_username : null,
    typeof detail.device_id === 'number' ? detail.device_id : null,
    reponse.status,
  );
}

/**
 * Message affichable pour n'importe quelle erreur d'API.
 *
 * Ordre : `detail` objet (message métier) > `detail` chaîne (contrat
 * historique des autres routes) > `err.message`. Le dernier recours
 * seulement, car sur un 4xx axios il ne contient jamais autre chose que
 * « Request failed with status code NNN ».
 */
export function messageErreurApi(erreur: any, repli = 'Erreur inattendue'): string {
  const detail = erreur?.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message) {
    return detail.message;
  }
  if (typeof detail === 'string' && detail) {
    return detail;
  }
  if (erreur?.message && !/^Request failed with status code/.test(erreur.message)) {
    return erreur.message;
  }
  return repli;
}


/**
 * LOT 9 — Écriture concurrente sur un boîtier (audit J-003).
 *
 * Deux opérateurs éditent la même fiche. Avant ce lot, les deux recevaient
 * 200 et l'un des deux perdait sa saisie sans jamais l'apprendre. Désormais
 * le perdant reçoit 409 `version_perimee`, avec la version courante et
 * l'état courant.
 *
 * `etatActuel` est le point important pour l'interface : il permet de
 * MONTRER ce qui a changé sans imposer un rechargement de page, qui ferait
 * perdre la saisie.
 */
export class ErreurVersionPerimee extends Error {
  readonly code: 'version_perimee' | 'version_requise' | 'boitier_supprime_entre_temps';
  readonly deviceId: number | null;
  readonly versionAttendue: number | null;
  readonly versionActuelle: number | null;
  readonly etatActuel: Record<string, any> | null;
  readonly statut: number;

  constructor(
    code: 'version_perimee' | 'version_requise' | 'boitier_supprime_entre_temps',
    message: string,
    deviceId: number | null,
    versionAttendue: number | null,
    versionActuelle: number | null,
    etatActuel: Record<string, any> | null,
    statut: number,
  ) {
    super(message);
    this.name = 'ErreurVersionPerimee';
    this.code = code;
    this.deviceId = deviceId;
    this.versionAttendue = versionAttendue;
    this.versionActuelle = versionActuelle;
    this.etatActuel = etatActuel;
    this.statut = statut;
    Object.setPrototypeOf(this, ErreurVersionPerimee.prototype);
  }

  /** Le formulaire doit-il rester ouvert ?
   *
   *  Oui pour un conflit de version : la saisie de l'opérateur est encore
   *  valable, il faut juste la rejouer sur la version courante. Non si le
   *  boîtier a été supprimé : il n'y a plus rien à enregistrer.
   */
  get saisieRecuperable(): boolean {
    return this.code !== 'boitier_supprime_entre_temps';
  }
}

const CODES_VERSION: readonly string[] = [
  'version_perimee',
  'version_requise',
  'boitier_supprime_entre_temps',
];

/**
 * Traduit un 409 / 428 / 404 de `PUT /devices/{id}` en `ErreurVersionPerimee`.
 * Rend `null` pour tout le reste — y compris un 409 d'identifiant MQTT, que
 * `conflitMqttDepuisErreur` traite déjà.
 */
export function conflitVersionDepuisErreur(erreur: any): ErreurVersionPerimee | null {
  const reponse = erreur?.response;
  if (!reponse) return null;
  if (![409, 428, 404].includes(reponse.status)) return null;

  const detail = reponse.data?.detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  if (typeof detail.code !== 'string' || !CODES_VERSION.includes(detail.code)) return null;

  const nombreOuNull = (v: any): number | null =>
    typeof v === 'number' ? v : null;

  return new ErreurVersionPerimee(
    detail.code as 'version_perimee' | 'version_requise' | 'boitier_supprime_entre_temps',
    typeof detail.message === 'string' && detail.message
      ? detail.message
      : 'Le boîtier a été modifié entre-temps.',
    nombreOuNull(detail.device_id),
    nombreOuNull(detail.version_attendue),
    nombreOuNull(detail.version_actuelle),
    detail.etat_actuel && typeof detail.etat_actuel === 'object'
      ? detail.etat_actuel
      : null,
    reponse.status,
  );
}
